<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import { useBlog, type BlogArticleListItem, type BlogCategoryItem } from '~/composables/useBlog'

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { fetchArticles, fetchCategories } = useBlog()

const slug = computed(() => route.params.slug as string)
const page = computed(() => Math.max(0, Number(route.query.page || 0)))
const pageSize = 12

const { data: result } = await useAsyncData(
  () => `blog-cat:${locale.value}:${slug.value}:${page.value}`,
  () => fetchArticles({ category: slug.value, page: page.value, size: pageSize }),
  { watch: [slug, page, locale] }
)

const { data: categories } = await useAsyncData<BlogCategoryItem[]>(() => `blog-categories:${locale.value}`, fetchCategories, { watch: [locale] })

const current = computed<BlogCategoryItem | undefined>(() => categories.value?.find(c => c.slug === slug.value))

const articles = computed<BlogArticleListItem[]>(() => result.value?.items ?? [])
const totalPages = computed<number>(() => result.value?.totalPages ?? 0)

const { canonicalUrl: canonical } = useSeoCanonical(() => `/blog/category/${slug.value}`)

const title = computed(() => current.value ? `${current.value.name} · ${t('blog.pageTitle')}` : t('blog.pageTitle'))

useHead({
  title,
  meta: [
    { name: 'description', content: () => current.value?.description || t('blog.pageDescription') },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: () => title.value },
    { property: 'og:url', content: () => canonical.value },
  ],
})

function formatDate(d?: string) {
  if (!d) return ''
  return new Date(d).toLocaleDateString()
}
</script>

<template>
  <DSContainer class="py-8">
    <header class="mb-10">
      <NuxtLink :to="localePath('/blog')" class="text-sm text-gray-500 hover:text-primary-600">← {{ t('blog.pageTitle') }}</NuxtLink>
      <h1 class="mt-3 text-4xl font-bold">{{ current?.name || slug }}</h1>
      <p v-if="current?.description" class="mt-3 text-lg text-gray-600 dark:text-gray-400">{{ current.description }}</p>
    </header>

    <div class="space-y-8">
      <article
        v-for="(article, idx) in articles"
        :key="article.id"
        class="group flex flex-col md:flex-row gap-6 pb-8 border-b border-gray-200 dark:border-gray-800 last:border-0"
      >
        <NuxtLink :to="localePath(`/blog/${article.slug}`)" class="md:w-56 flex-shrink-0 aspect-video md:aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 block">
          <NuxtImg
            v-if="article.coverImageUrl"
            :src="article.coverImageUrl"
            :alt="article.title"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform"
            width="400"
            height="300"
            :loading="idx === 0 ? 'eager' : 'lazy'"
          />
        </NuxtLink>
        <div class="flex-1">
          <h2 class="text-xl font-bold leading-tight">
            <NuxtLink :to="localePath(`/blog/${article.slug}`)" class="hover:text-primary-600">{{ article.title }}</NuxtLink>
          </h2>
          <p v-if="article.excerpt" class="mt-2 text-gray-600 dark:text-gray-400 line-clamp-2">{{ article.excerpt }}</p>
          <div class="mt-3 text-sm text-gray-500">
            <time>{{ formatDate(article.publishedAt) }}</time>
            <span v-if="article.readingMinutes"> · {{ t('blog.minutesRead', { n: article.readingMinutes }) }}</span>
          </div>
        </div>
      </article>
      <div v-if="articles.length === 0" class="py-16 text-center text-gray-500">{{ t('blog.empty') }}</div>
    </div>

    <nav v-if="totalPages > 1" class="flex justify-center gap-2 pt-8" aria-label="pagination">
      <NuxtLink v-if="page > 0" :to="{ path: localePath(`/blog/category/${slug}`), query: { page: page - 1 } }" class="px-4 py-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
        {{ t('blog.previous') }}
      </NuxtLink>
      <span class="px-4 py-2 text-sm text-gray-500">{{ page + 1 }} / {{ totalPages }}</span>
      <NuxtLink v-if="page < totalPages - 1" :to="{ path: localePath(`/blog/category/${slug}`), query: { page: page + 1 } }" class="px-4 py-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
        {{ t('blog.next') }}
      </NuxtLink>
    </nav>
  </DSContainer>
</template>
